import { ExpandLess, ExpandMore, SaveOutlined } from "@mui/icons-material";
import {
  Collapse,
  IconButton,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
} from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import type { SlaEditorTarget } from "model/slaEditorModel";
import React, { useState } from "react";
import { useSlaModelDraftContext, useSlaModelSave } from "./slaModelDraftContext";
import SlaTargetForm from "./SlaTargetForm";

/**
 * Sezione richiudibile "SLA" del pannello proprieta' del designer, con lo stesso
 * schema di "Proprieta' primarie"/"Proprieta' specifiche" (`ListItemButton` +
 * `Collapse`). Senza SLA attivo nell'organizzazione non si rende.
 *
 * Accanto al titolo c'e' il pulsante di salvataggio del modello (stesso handler
 * della toolbar, `useSlaModelSave`): le definizioni SLA si salvano solo con il
 * modello e l'utente non sempre lo sa.
 */
interface SlaDesignerSectionProps {
  target: SlaEditorTarget;
}

const SlaDesignerSection = ({ target }: SlaDesignerSectionProps) => {
  const translations = useTranslations();
  const sla = useSlaModelDraftContext();
  const saveModel = useSlaModelSave();
  const [open, setOpen] = useState<boolean>(true);

  if (!sla?.available) return null;

  return (
    <React.Fragment>
      <Stack direction="row" alignItems="center">
        <ListItemButton
          id="sla-designer-section"
          sx={{ flex: 1 }}
          onClick={() => setOpen((prev) => !prev)}
        >
          <ListItemText primary={getTranslation(translations, "pm.propertyeditor.sla")} />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        {saveModel && (
          <Tooltip title={getTranslation(translations, "pm.buttons.action.save.revision")}>
            <span>
              <IconButton
                size="small"
                id="sla-designer-save-btn"
                disabled={sla.saving}
                onClick={saveModel}
              >
                <SaveOutlined color={sla.saving ? "disabled" : "primary"} fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
      <Collapse sx={{ pt: 2 }} timeout="auto" unmountOnExit in={open}>
        <SlaTargetForm target={target} />
      </Collapse>
    </React.Fragment>
  );
};

export default SlaDesignerSection;
